import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { IoAddOutline, IoCloseCircle, IoSearchOutline } from 'react-icons/io5';
import { FaRegEdit } from "react-icons/fa";
import { MdOutlineDelete } from "react-icons/md";
import { BsCalendar4 } from "react-icons/bs";
import { useUser } from '../../context/UserContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { duration } from '@mui/material';
import { useSearchParams } from 'react-router';
import { useCustomDialog } from '../../context/DialogProvider';
import { subDays } from 'date-fns';
import api from '../../utils/api';
import { LuCalendarMinus } from 'react-icons/lu';


const START_TIMES = [
  { value: "09:30 AM", label: "09:30 AM" },
  { value: "01:30 PM", label: "01:30 PM" }
];

const END_TIMES = [
  { value: "01:30 PM", label: "01:30 PM" },
  { value: "06:30 PM", label: "06:30 PM" }
];

function E_Leave() {

  const baseUrl = process.env.REACT_APP_API_URL;
  const { currentUser, loading } = useUser();
  const employeeId = currentUser?.id;
  const leavesPerPage = 6;

  const [currentPage, setCurrentPage] = useState(1);
  const [leaves, setLeaves] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editLeave, setEditLeave] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLeave, setViewLeave] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [holidays, setHolidays] = useState([])
  const [searchParams] = useSearchParams();
  const [leaveBalance, setLeaveBalance] = useState({})
  const searchTerm = (searchParams.get("search") || "").toLowerCase();
  const { confirm } = useCustomDialog();
  const [isBackdatedLeaveForEdit, setIsBackdatedLeaveForEdit] = useState(false);


  const [newLeave, setNewLeave] = useState({
    leaveType: 'Earned Leave',
    startDate: null,
    endDate: null,
    startDateTime: "09:30 AM",
    endDateTime: "06:30 PM",
    duration: 0,
    reason: ''
  });

  const isBackdatedLeave = ['Sick Leave', 'Menstrual Leave'].includes(newLeave.leaveType);
  const isExtendedLeave = ['Earned Leave', 'Comp Off Leave', 'LOP Leave'].includes(newLeave.leaveType);




  const [isLoading, setIsLoading] = useState(true);

  const getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of the day
    return today;
  };

  const getLeaveEndDateNormalized = (dateString) => {
    // Parsing "YYYY-MM-DD" directly usually works well for Date constructor
    const leaveEnd = new Date(dateString);
    // Set time to 00:00:00.000 for accurate day-level comparison
    leaveEnd.setHours(0, 0, 0, 0);
    return leaveEnd;
  };

  const today = getTodayDate();




  const areSameDates = (date1, date2) => {
    if (!date1 || !date2) return false;

    const d1 = date1 instanceof Date ? date1 : new Date(date1);
    const d2 = date2 instanceof Date ? date2 : new Date(date2);

    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;

    return (
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear()
    );
  };


  const calculateDuration = (startDate, endDate, startDateTime, endDateTime, holidays, leaveType) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

    const holidaySet = new Set(holidays.map(h => h.holidayDate));
    const dates = [];
    let duration = 0;

    // Prepare full date range
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }

    const isNonWorkingDay = (date) => {
      const formatted = formatLocalDate(date);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isHoliday = holidaySet.has(formatted);
      return isWeekend || isHoliday;
    };

    // Loop all dates
    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      const prev = dates[i - 1];
      const next = dates[i + 1];

      if (!isNonWorkingDay(date)) {
        duration += 1;
      } else {
        const isSandwiched =
          prev && next && !isNonWorkingDay(prev) && !isNonWorkingDay(next);
        if (!isSandwiched) {
          duration += 1;
        }
      }
    }

    // Half-day logic for multi-day leaves
    if (dates.length === 1) {
      // Single day
      if (startDateTime === "01:30 PM" || endDateTime === "01:30 PM") return 0.5;
      return 1;
    } else {
      // Multi-day
      if (startDateTime === "01:30 PM") duration -= 0.5; // First day is half
      if (endDateTime === "01:30 PM") duration -= 0.5;   // Last day is half
    }

    return duration;
  };




  const fetchLeaves = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/leaves/${employeeId}`);
      setLeaves(response.data);
    } catch (error) {
      console.error("Error fetching leaves:", error);
      toast.error('Failed to fetch leave requests');
    } finally {
      setIsLoading(false);
    }
  };


  const cancelLeave = async (leaveId) => {
    if (await confirm('Are you sure you want to cancel this leave request?')) {
      try {
        const res = await api.put(`/api/leaves/cancel/${leaveId}`);
        toast.success(res.data)
        setShowViewModal(false)
        fetchLeaves();
      } catch (error) {
        toast.error("Error fetching leaves:", error);
      }
    }
  };



  const fetchHolidays = async () => {
    setFetching(true);
    try {
      const response = await api.get(`/api/holidays`);
      setHolidays(response.data);
      console.log(response.data)
    } catch (error) {
      console.error("Error fetching holidays:", error);
    } finally {
      setFetching(false);
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const response = await api.get(`/leaveBalance/${currentUser?.id}`);
      setLeaveBalance(response.data)
    } catch (error) {
      setLeaveBalance(
        {
          "earnedLeaves": "-",
          "sickLeaves": "-",
          "compOffs": "-"
        }
      )
    }
  }

  useEffect(() => {
    fetchLeaveBalance();
  }, []);




  useEffect(() => {
    fetchHolidays();
  }, []);


  useEffect(() => {

    if (employeeId) {
      fetchLeaves();
    }
  }, [employeeId]);





  const handleApplyLeave = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const { leaveType, startDate, endDate, reason } = newLeave;

    // ✅ 1️⃣ Basic validation
    if (!leaveType || !startDate || !endDate || !reason.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (endDate < startDate) {
      toast.error("End date cannot be before start date.");
      return;
    }

    // ✅ 2️⃣ Calculate duration first
    const duration = calculateDuration(
      newLeave.startDate,
      newLeave.endDate,
      newLeave.startDateTime,
      newLeave.endDateTime,
      holidays,
      newLeave.leaveType
    );

    // ✅ 3️⃣ Validate leave balance
    let availableBalance = 0;

    if (leaveType === "Earned Leave" || leaveType === "Emergency Leave") {
      availableBalance = leaveBalance.earnedLeaves;
    } else if (leaveType === "Sick Leave") {
      availableBalance = leaveBalance.sickLeaves;
    } else if (leaveType === "Comp Off Leave") {
      availableBalance = leaveBalance.compOffs;
    }
    else if (leaveType == "LOP Leave") {
      availableBalance = duration;
    }
    else if (leaveType === "Menstrual Leave") {
      availableBalance = 1;
    }

    if (leaveType === "Menstrual Leave" && duration > 1) {
      toast.error("You can only apply for a maximum of 1 day for Menstrual Leave.");
      return;
    }

    if (currentUser?.office !== "ABB" && leaveType !== "Menstrual Leave" && duration > availableBalance) {
      toast.error(
        `Insufficient balance. You have only ${availableBalance} ${leaveType} day${availableBalance !== 1 ? "s" : ""
        } left.`
      );
      return;
    }

    setIsSubmitting(true);

    // ✅ 4️⃣ Prepare data
    const formatDateForBackend = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const payload = {
      ...newLeave,
      startDate: formatDateForBackend(newLeave.startDate),
      endDate: formatDateForBackend(newLeave.endDate),
      duration,
    };

    try {
      console.log(payload);
      const response = await api.post(
        `/api/leaves/apply?employeeId=${employeeId}`,
        payload
      );

      toast.success("Leave applied successfully");

      setShowApplyForm(false);
      setNewLeave({
        leaveType: "Earned Leave",
        startDate: null,
        endDate: null,
        startDateTime: "09:30 AM",
        endDateTime: "06:30 PM",
        duration: 0,
        reason: "",
      });
      fetchLeaves();
      fetchLeaveBalance();
    } catch (error) {
      console.error("Error applying leave:", error);
      const errorMessage = error.response?.data?.message || error.response?.data || "Failed to apply for leave";
      toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to apply for leave");
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleEdit = (leave) => {
    setEditLeave({
      id: leave.leaveRequestId,
      leaveType: leave.leaveType,
      startDate: leave.leaveStartDate,
      startDateTime: leave.startDateTime,
      endDateTime: leave.endDateTime,
      endDate: leave.leaveEndDate,
      duration: leave.duration,
      reason: leave.reason
    });
    setIsBackdatedLeaveForEdit(['Sick Leave', 'Emergency Leave', 'Menstrual Leave'].includes(leave.leaveType));
    console.log("Editing leave:", isBackdatedLeaveForEdit);
    setShowEditForm(true);
  };


  const handleDelete = async (leaveId) => {
    if (window.confirm('Are you sure you want to delete this leave request?')) {
      try {
        console.log("Deleting leave with ID:", employeeId, leaveId);
        await api.delete(`/api/leaves/${leaveId}?employeeId=${employeeId}`);
        toast.success("Leave request deleted successfully");
        fetchLeaves();
      } catch (error) {
        console.error("Error deleting leave:", error);
        if (error.response?.status === 403) {
          toast.error("You are not authorized to delete this leave request");
        } else if (error.response?.status === 404) {
          toast.error("Leave request not found");
        } else {
          toast.error("Failed to delete leave request");
        }
      }
    }
  };


  const handleEditSubmit = async (e) => {
    e.preventDefault();
    // Format dates as YYYY-MM-DD to avoid timezone issues
    const formatDateForBackend = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    try {
      const duration = calculateDuration(
        editLeave.startDate,
        editLeave.endDate,
        editLeave.startDateTime,
        editLeave.endDateTime,
        holidays,
        editLeave.leaveType
      );
      const payload = {
        leaveType: editLeave.leaveType,
        startDate: formatDateForBackend(editLeave.startDate),
        endDate: formatDateForBackend(editLeave.endDate),
        startDateTime: editLeave.startDateTime,
        endDateTime: editLeave.endDateTime,
        duration: duration,
        reason: editLeave.reason
      };
      const response = await api.put(
        `/api/leaves/${editLeave.id}?employeeId=${employeeId}`,
        payload
      );
      toast.success("Leave updated successfully");
      setShowEditForm(false);
      fetchLeaves();
    } catch (error) {
      console.error("Error updating leave:", error);
      if (error.response?.status === 403) {
        toast.error("You are not authorized to edit this leave request");
      } else if (error.response?.status === 404) {
        toast.error("Leave request not found");
      } else {
        const errorMessage = error.response?.data?.message || error.response?.data;
        toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to update leave request");
      }
    }
  };


  const handleStartDateChange = (date) => {
    const updatedLeave = {
      ...newLeave,
      startDate: date,
      endDate: newLeave.leaveType === "Menstrual Leave" 
               ? date 
               : (newLeave.endDate && date && new Date(date) <= new Date(newLeave.endDate) ? newLeave.endDate : date),
      endDateTime: areSameDates(date, newLeave.endDate || date) && newLeave.startDateTime >= newLeave.endDateTime ? "06:30 PM" : newLeave.endDateTime
    };

    if (updatedLeave.startDate && updatedLeave.endDate) {
      updatedLeave.duration = calculateDuration(
        updatedLeave.startDate,
        updatedLeave.endDate,
        updatedLeave.startDateTime,
        updatedLeave.endDateTime,
        holidays,
        updatedLeave.leaveType
      );
    }

    setNewLeave(updatedLeave);
  };


  const handleEndDateChange = (date) => {
    // Prevent selecting end date before start date
    if (newLeave.startDate && date && new Date(date) < new Date(newLeave.startDate)) {
      return;
    }
    const updatedLeave = {
      ...newLeave,
      endDate: date
    };

    if (updatedLeave.startDate && updatedLeave.endDate) {
      updatedLeave.duration = calculateDuration(
        updatedLeave.startDate,
        updatedLeave.endDate,
        updatedLeave.startDateTime,
        updatedLeave.endDateTime,
        holidays,
        updatedLeave.leaveType
      );
    }
    setNewLeave(updatedLeave);
  };


  const handleView = (leave) => {
    setViewLeave(leave);
    setShowViewModal(true);
  };


  const handleEditLeaveTypeChnage = (e) => {
    setEditLeave({ ...editLeave, leaveType: e.target.value })
    setIsBackdatedLeaveForEdit(['Sick Leave', 'Emergency Leave', 'Menstrual Leave'].includes(e.target.value));
  }


  const truncateText = (text, limit) => {
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
  };


  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = (`0${date.getMonth() + 1}`).slice(-2); // months are 0-indexed
    const day = (`0${date.getDate()}`).slice(-2);
    return `${year}-${month}-${day}`;
  };


  const isWeekdayAndNotHoliday = (date, holidays = []) => {
    if (!date || !Array.isArray(holidays)) return true;

    const day = date.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = day === 0 || day === 6;

    const formatted = formatLocalDate(date); // 👈 Use local time

    const isHoliday = holidays.some(holiday => holiday.holidayDate === formatted);

    return !isWeekend && !isHoliday;
  };



  if (loading) {
    return (
      <div className="sm:p-6 sm:mt-auto mt-8 am:mb-auto mb-20 w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-32 bg-slate-200 rounded-lg animate-pulse"></div>
          <div className="h-10 w-32 bg-slate-200 rounded-full animate-pulse"></div>
        </div>
        <div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="h-[400px] flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
        <div className="sm:hidden space-y-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="bg-white p-5 rounded-xl shadow-md border border-slate-100">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="h-6 w-24 bg-slate-200 rounded animate-pulse"></div>
                  <div className="h-6 w-6 bg-slate-200 rounded-full animate-pulse"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 w-full bg-slate-200 rounded animate-pulse"></div>
                  <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const filteredLeaves = leaves.filter((p) =>
    p.leaveType.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.reason.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredLeaves.length / leavesPerPage);
  const paginatedLeaves = filteredLeaves.slice(
    (currentPage - 1) * leavesPerPage,
    currentPage * leavesPerPage
  );

  const duration =
    newLeave.startDate && newLeave.endDate
      ? calculateDuration(
        newLeave.startDate,
        newLeave.endDate,
        newLeave.startDateTime,
        newLeave.endDateTime,
        holidays
      )
      : null;

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 am:mb-auto mb-20 w-full mx-auto ">

      <motion.div
        className="flex justify-between items-center mb-6 bg-blue-100 rounded-2xl p-6 border blue-slate-300"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-4 text-xl xs:text-2xl  font-bold truncate">
          <div className="p-2 rounded-lg bg-blue-900 text-slate-100" ><LuCalendarMinus /></div>
          <h2 className="">My Leaves</h2>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 gap-3 items-center ">
          <motion.button
            onClick={() => setShowApplyForm(true)}
            className="bg-slate-800 text-white px-4 py-2 rounded-full hover:bg-slate-700 flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <IoAddOutline className="text-lg" />
            <span>Apply</span>
          </motion.button>

        </div>

      </motion.div>

      {/* Leave Application Modal */}
      {showApplyForm && (
        <div className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 
                 max-h-[97vh] overflow-y-auto flex flex-col"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-bold">Apply for Leave</h3>
              <button
                onClick={() => {
                  if (!isSubmitting) {
                    setShowApplyForm(false);
                    setNewLeave({
                      leaveType: 'Earned Leave',
                      startDate: null,
                      endDate: null,
                      startDateTime: "09:30 AM",
                      endDateTime: "06:30 PM",
                      duration: 0,
                      reason: ''
                    });
                  }
                }}
                disabled={isSubmitting}
                className={`text-slate-400 hover:text-slate-600 ${isSubmitting ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                ✕
              </button>
            </div>

            {/* Form Scrollable Content */}
            <form className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
              {/* Leave Balances Block */}
              <div className="bg-gradient-to-r from-slate-100 to-slate-200 shadow-md rounded-2xl p-3 flex items-center justify-between gap-4">
                {[
                  { label: 'Remaining EL', value: leaveBalance.earnedLeaves, color: 'bg-blue-100 text-blue-700' },
                  { label: 'Remaining SL', value: leaveBalance.sickLeaves, color: 'bg-green-100 text-green-700' },
                  { label: 'Comp Off', value: leaveBalance.compOffs, color: 'bg-yellow-100 text-yellow-700' },
                ].map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center justify-center w-full gap-1">
                    <span className="text-xs font-medium text-slate-700">{item.label}</span>
                    <div className={`rounded-xl px-4 py-2 text-sm font-semibold ${item.color} shadow-inner`}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Leave Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Type</label>
                <select
                  value={newLeave.leaveType}
                  onChange={(e) => setNewLeave({ ...newLeave, leaveType: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                  required
                >
                  <option value="Earned Leave">Earned / Emergency Leave</option>
                  <option value="Sick Leave">Sick Leave</option>
                  <option value="Comp Off Leave">Comp Off Leave</option>
                  <option value="LOP Leave">LOP Leave</option>
                  <option value="Menstrual Leave">Menstrual Leave</option>
                </select>
              </div>

              {/* Date and Time Pickers */}
              <div className="grid grid-cols-2 gap-4">
                {/* Start */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Date & Time</label>
                  <div className="space-y-2">
                    <div className="relative">
                      <DatePicker
                        selected={newLeave.startDate}
                        onChange={handleStartDateChange}
                        dateFormat="dd-MM-yyyy"
                        className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
                        required
                        minDate={
                          isBackdatedLeave || isExtendedLeave
                            ? new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)
                            : new Date()
                        }
                        maxDate={
                          isBackdatedLeave
                            ? new Date()
                            : null
                        }
                        placeholderText="DD-MM-YYYY"
                        filterDate={date => isWeekdayAndNotHoliday(date, holidays)}
                      />
                      <BsCalendar4 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                    <select
                      value={newLeave.startDateTime}
                      onChange={(e) => setNewLeave({ ...newLeave, startDateTime: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                    >
                      {START_TIMES.map(time => (
                        <option key={time.value} value={time.value}>{time.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* End */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Date & Time</label>
                  <div className="space-y-2">
                    <div className="relative">
                      <DatePicker
                        selected={newLeave.endDate}
                        onChange={handleEndDateChange}
                        dateFormat="dd-MM-yyyy"
                        className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
                        required
                        minDate={newLeave.startDate || new Date()}
                        maxDate={
                          newLeave.leaveType === 'Menstrual Leave' && newLeave.startDate
                            ? new Date(newLeave.startDate)
                            : isBackdatedLeave ? new Date() : null
                        }
                        placeholderText="DD-MM-YYYY"
                        filterDate={date => isWeekdayAndNotHoliday(date, holidays)}
                      />
                      <BsCalendar4 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                    <select
                      value={newLeave.endDateTime}
                      onChange={(e) => setNewLeave({ ...newLeave, endDateTime: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                    >
                      {END_TIMES.map(time => (
                        <option key={time.value} value={time.value}>{time.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Duration */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Duration</label>
                <div className="w-full border border-slate-200 rounded-lg p-2.5 bg-slate-50">
                  {newLeave.startDate && newLeave.endDate ? (
                    <span>
                      {duration}
                      {' day' + (duration === 1 || duration === 0.5 ? '' : 's')}
                    </span>
                  ) : '—'}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Duration is calculated based on selected dates and times
                </p>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                <textarea
                  value={newLeave.reason}
                  onChange={(e) => setNewLeave({ ...newLeave, reason: e.target.value })}
                  rows="3"
                  className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                  required
                ></textarea>
              </div>
            </form>

            {/* Footer Buttons */}
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowApplyForm(false);
                  setNewLeave({
                    leaveType: 'Earned Leave',
                    startDate: null,
                    endDate: null,
                    startDateTime: "09:30 AM",
                    endDateTime: "06:30 PM",
                    duration: 0,
                    reason: ''
                  });
                }}
                className="px-4 py-2 border-2 rounded-full hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleApplyLeave}
                disabled={isSubmitting}
                className="bg-slate-800 text-white px-6 py-2 rounded-full hover:bg-slate-700 
                     disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}


      {/* Edit Leave Modal */}
      {showEditForm && editLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="text-xl font-bold mb-4">Edit Leave Request</h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Type</label>
                <select
                  value={editLeave.leaveType}
                  onChange={handleEditLeaveTypeChnage}
                  className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                  required
                >
                  <option value="Earned Leave">Earned Leave</option>
                  <option value="Sick Leave">Sick Leave</option>
                  <option value="Comp Off Leave">Comp Off Leave</option>
                  <option value="Emergency Leave">Emergency Leave</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Date & Time</label>
                  <div className="space-y-2">
                    <div className="relative">
                      <DatePicker
                        selected={editLeave.startDate}
                        onChange={(date) => setEditLeave({ ...editLeave, startDate: date })}
                        dateFormat="dd-MM-yyyy"
                        className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
                        required
                        minDate={isBackdatedLeaveForEdit ? new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1) : new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)} // Previous month 1st or today onwards
                        maxDate={isBackdatedLeaveForEdit ? new Date() : null} // Today or unlimited future 
                        placeholderText="DD-MM-YYYY"
                        filterDate={date => isWeekdayAndNotHoliday(date, holidays)}
                      />
                      <BsCalendar4 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                    <select
                      value={editLeave.startDateTime}
                      onChange={(e) => setEditLeave({ ...editLeave, startDateTime: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                    >
                      {START_TIMES.map(time => (
                        <option key={time.value} value={time.value}>{time.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Date & Time</label>
                  <div className="space-y-2">
                    <div className="relative">
                      <DatePicker
                        selected={editLeave.endDate}
                        onChange={(date) => setEditLeave({ ...editLeave, endDate: date })}
                        dateFormat="dd-MM-yyyy"
                        className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
                        required
                        minDate={editLeave.startDate || new Date()}
                        maxDate={
                          editLeave.leaveType === 'Menstrual Leave' && editLeave.startDate
                            ? new Date(editLeave.startDate)
                            : isBackdatedLeaveForEdit ? new Date() : null
                        }
                        placeholderText="DD-MM-YYYY"
                        filterDate={date => isWeekdayAndNotHoliday(date, holidays)}
                      />
                      <BsCalendar4 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                    <select
                      value={editLeave.endDateTime}
                      onChange={(e) => setEditLeave({ ...editLeave, endDateTime: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                    >
                      {END_TIMES.map(time => (
                        <option key={time.value} value={time.value}>{time.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Duration Display */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Duration</label>
                <div className="w-full border border-slate-200 rounded-lg p-2.5 bg-slate-50">
                  {editLeave.startDate && editLeave.endDate ? (
                    <span>
                      {calculateDuration(editLeave.startDate, editLeave.endDate, editLeave.startDateTime, editLeave.endDateTime, holidays, editLeave.leaveType)}
                      {' day' + (calculateDuration(editLeave.startDate, editLeave.endDate, editLeave.startDateTime, editLeave.endDateTime, holidays, editLeave.leaveType) === 1 || 0.5 ? '' : 's')}
                    </span>
                  ) : '—'}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Duration is calculated based on selected dates and times
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                <textarea
                  value={editLeave.reason}
                  onChange={(e) => setEditLeave({ ...editLeave, reason: e.target.value })}
                  rows="3"
                  className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                  required
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  className="px-4 py-2 border-2 rounded-full hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-slate-800 text-white px-6 py-2 rounded-full hover:bg-slate-700"
                >
                  Update
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}


      {/* View Leave Modal */}
      {showViewModal && viewLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Leave Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-slate-400 hover:text-slate-500"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Leave Type</label>
                  <p className="mt-1 text-slate-800">{viewLeave.leaveType}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-500">Leave duration</label>
                  <p className="mt-1 text-slate-800">
                    {viewLeave.duration} {viewLeave.duration === 1 || viewLeave.duration === 0.5 ? "day" : "days"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Start Date</label>
                  <p className="mt-1 text-slate-800">
                    {formatDate(viewLeave.leaveStartDate)} ({viewLeave.startDateTime})
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">End Date</label>
                  <p className="mt-1 text-slate-800">{formatDate(viewLeave.leaveEndDate)} ({viewLeave.endDateTime})</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">Status</label>
                <p className="mt-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${viewLeave.leaveStatus === "APPROVED"
                    ? "bg-green-100 text-green-800"
                    : viewLeave.leaveStatus === "PENDING"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                    }`}>
                    {viewLeave.leaveStatus}
                  </span>
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">Reason</label>
                <p className="mt-1 text-slate-800">{viewLeave.reason}</p>
              </div>

              {viewLeave.approverComment && (
                <div>
                  <label className="text-sm font-medium text-slate-500">Manager Comment</label>
                  <p className="mt-1 text-slate-800">{viewLeave.approverComment}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-2">
              {viewLeave.leaveStatus !== "CANCELLED" && viewLeave.leaveStatus !== "REJECTED" && today <= getLeaveEndDateNormalized(viewLeave.leaveEndDate) &&
                (
                  <button
                    onClick={() => {
                      console.log(today)
                      cancelLeave(viewLeave.leaveRequestId)
                    }}
                    className="w-full px-4 py-2 bg-slate-800 text-white rounded-full hover:bg-slate-700"
                  >
                    Cancel leave
                  </button>
                )

              }



              <button
                onClick={() => setShowViewModal(false)}
                className="w-full px-4 py-2 bg-slate-700 text-white rounded-full hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Desktop View */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Sl. No</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Type</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Start Date & Time</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">End Date & Time</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Status</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Reason</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-slate-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : paginatedLeaves.length > 0 ? (
                paginatedLeaves.map((leave, index) => (
                  <tr key={leave.leaveRequestId} className="hover:bg-slate-50/50 transition-colors duration-150">
                    <td className="px-6 py-4 text-slate-600">
                      {(currentPage - 1) * leavesPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-700">{leave.leaveType}</td>
                    <td className="px-6 py-4 text-slate-600">{formatDate(leave.leaveStartDate)} ({leave.startDateTime})</td>
                    <td className="px-6 py-4 text-slate-600">{formatDate(leave.leaveEndDate)} ({leave.endDateTime})</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${leave.leaveStatus === "APPROVED"
                        ? "bg-green-100 text-green-800"
                        : leave.leaveStatus === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                        }`}>
                        {leave.leaveStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600" title={leave.reason}>
                      {truncateText(leave.reason, 10)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-3">
                        {leave.leaveStatus !== "PENDING" && (
                          <motion.button
                            onClick={() => handleView(leave)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="View Leave"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </motion.button>
                        )}
                        {leave.leaveStatus === "PENDING" && (
                          <>
                            <motion.button
                              onClick={() => handleEdit(leave)}
                              className="text-yellow-600 hover:text-yellow-800 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="Edit Leave"
                            >
                              <FaRegEdit className="text-xl" />
                            </motion.button>
                            <motion.button
                              onClick={() => handleDelete(leave.leaveRequestId)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="Delete Leave"
                            >
                              <MdOutlineDelete className="text-xl" />
                            </motion.button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-slate-400">
                    No leave requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#fff",
                },
              }}
            />
          </Stack>
        </div>
      </motion.div>

      {/* Mobile View */}
      <div className="sm:hidden space-y-4">
        {isLoading ? (
          <div className="bg-white p-5 rounded-xl shadow-md border border-slate-100">
            <div className="flex justify-center items-center py-8">
              <svg
                className="animate-spin h-8 w-8 text-slate-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            </div>
          </div>
        ) : paginatedLeaves.length > 0 ? (
          paginatedLeaves.map((leave, index) => (
            <motion.div
              key={leave.leaveRequestId}
              className="bg-white p-5 rounded-xl shadow-md border border-slate-100"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-500">#{index + 1}</span>
                    <h3 className="font-semibold text-slate-800">{leave.leaveType}</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    {leave.leaveStatus === "PENDING" ? (
                      <div className="flex gap-2">
                        <motion.button
                          onClick={() => handleEdit(leave)}
                          className="text-yellow-600 hover:text-yellow-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <FaRegEdit className="text-lg" />
                        </motion.button>
                        <motion.button
                          onClick={() => handleDelete(leave.leaveRequestId)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <MdOutlineDelete className="text-lg" />
                        </motion.button>
                      </div>
                    ) : (
                      <motion.button
                        onClick={() => handleView(leave)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </motion.button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Start Date:</span>
                    <span className="text-slate-700">{formatDate(leave.leaveStartDate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">End Date:</span>
                    <span className="text-slate-700">{formatDate(leave.leaveEndDate)}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-500">Reason: </span>
                    <span className="text-slate-700" title={leave.reason}>
                      {truncateText(leave.reason, 10)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="bg-white p-8 rounded-xl text-center text-slate-400">
            No leave requests found
          </div>
        )}

        {/* Mobile Pagination */}
        <div className="mt-6 flex justify-center">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="medium"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#fff",
                },
              }}
            />
          </Stack>
        </div>
      </div>
    </div>
  );
}

export default E_Leave;
